import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Heart,
  ShoppingCart,
  User,
  Phone,
  // Mail,
  Menu,
  Loader2,
  LogOut,
  Truck,
} from "lucide-react";
import {
  useCategoriesWithProducts,
  getUserCardGetV2,
} from "../../hooks/useSupabase";
import { getProducts } from "../../services/products.service";
import { useAuth } from "../../hooks/useAuth";
import { useWishlistContext } from "../../hooks/useWishlistContext";

import MobileHeader from "./MobileHeader";
import { getPrimaryProductImageUrl } from "../../utils";
import Categories from "./Categories";

const Header = ({
  isCategoriesVisible = true,
}: {
  isCategoriesVisible?: boolean;
}) => {
  const navigate = useNavigate();
  const { user, signOut, userId } = useAuth();

  // State hooks
  const [isMobile, setIsMobile] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Add custom hooks for cart and wishlist counts
  const { wishlistedIds } = useWishlistContext();
  const wishlistCount = wishlistedIds.length;

  // Custom cart hook implementation
  const [cartCount, setCartCount] = useState(0);
  const [cartLoading, setCartLoading] = useState(false);

  useEffect(() => {
    const fetchCartCount = async () => {
      if (!userId) {
        setCartCount(0);
        return;
      }

      setCartLoading(true);
      try {
        const response = await getUserCardGetV2({});
        const typedResponse = response as any;

        if (typedResponse && typedResponse.data && typedResponse.data.items) {
          const totalItems = typedResponse.data.items.reduce(
            (sum: number, item: any) => {
              return sum + (item.qty || 0);
            },
            0,
          );
          setCartCount(totalItems);
        } else {
          setCartCount(0);
        }
      } catch (error) {
        setCartCount(0);
      } finally {
        setCartLoading(false);
      }
    };

    fetchCartCount();
  }, [userId]);
  const {
    categoriesWithSubs,
    loading: categoriesLoading,
    error: categoriesError,
  } = useCategoriesWithProducts();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".user-menu-container")) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isUserMenuOpen]);

  // Search effect
  useEffect(() => {
    setSearchLoading(true);
    const delayedSearch = setTimeout(() => {
      if (searchQuery.length > 2) {
        if (searchQuery.trim()) {
          getProducts({ search: searchQuery.trim(), limit: 20, offset: 0 })
            .then(({ products }) => {
              setSearchResults(products);
              setShowSearchResults(true);
              setSearchLoading(false);
            })
            .catch(() => {
              setSearchResults([]);
              setShowSearchResults(false);
              setSearchLoading(false);
            });
        }
      } else {
        setShowSearchResults(false);
        setSearchResults([]);
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(delayedSearch);
      setSearchLoading(false);
    };
  }, [searchQuery]);

  const handleWishlistClick = () => {
    if (!user) {
      navigate("/registration");
      return;
    }
    navigate("/wishlist");
  };

  const handleCartClick = () => {
    if (!user) {
      navigate("/registration");
      return;
    }
    navigate("/cart");
  };

  const handleTrackingClick = () => {
    if (!user) {
      navigate("/registration");
      return;
    }
    navigate("/tracking");
  };

  const handleSearchEnter = async (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter" && searchQuery.length > 2) {
      // Clear search results and close dropdown
      setShowSearchResults(false);

      // Navigate to products page with search query - let ProductPage handle the search
      navigate(
        `/products?search=${encodeURIComponent(searchQuery)}&sort=popular`,
        {
          state: {
            searchQuery: searchQuery,
            sort: "popular",
          },
        },
      );

      // Clear the search input
      setSearchQuery("");
    }
  };

  // If mobile, render MobileHeader
  if (isMobile) {
    return <MobileHeader />;
  }
  return (
    <div className="w-full bg-neutral-900 shadow-md">
      {/* Top Header - Enhanced Design */}
      <div className="bg-neutral-900 text-white py-2.5 px-4 hidden md:block border-b border-neutral-800">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center space-x-4 lg:space-x-8">
            <a
              href="tel:+96181441597"
              className="flex items-center space-x-2 hover:text-primary transition-colors group"
            >
              <div className="p-1.5 bg-white/10 rounded-full group-hover:bg-primary/20 transition-colors">
                <Phone className="w-3.5 h-3.5" />
              </div>
              <span className="hidden lg:block font-medium">
                +961 76 700 424
              </span>
              <span className="lg:hidden font-medium">Call Us</span>
            </a>
            {/* <a
              href="mailto:yaghi_store@outlook.com"
              className="flex items-center space-x-2 hover:text-primary transition-colors group"
            >
              <div className="p-1.5 bg-white/10 rounded-full group-hover:bg-primary/20 transition-colors">
                <Mail className="w-3.5 h-3.5" />
              </div>
              <span className="hidden lg:block font-medium">
                yaghi_store@outlook.com
              </span>
              <span className="lg:hidden font-medium">Email</span>
            </a> */}
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-300 font-medium">Follow us:</span>
            <div className="flex space-x-2.5">
              <a
                href="https://www.facebook.com/people/blackstorelb24/61559582609836/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 hover:scale-110 transition-all duration-200 shadow-sm"
                aria-label="Facebook"
              >
                <i className="fab fa-facebook-f text-white text-sm"></i>
              </a>
              <a
                href="https://www.instagram.com/blackstorelb24"
                target="_blank"
                rel="noopener noreferrer"
                className="w-7 h-7 bg-linear-to-br from-purple-600 to-pink-500 rounded-full flex items-center justify-center hover:scale-110 transition-all duration-200 shadow-sm"
                aria-label="Instagram"
              >
                <i className="fab fa-instagram text-white text-sm"></i>
              </a>
              {/* <a
                href="https://www.tiktok.com/@yaghi.store?is_from_webapp=1&sender_device=pc"
                target="_blank"
                rel="noopener noreferrer"
                className="w-7 h-7 bg-neutral-900 rounded-full flex items-center justify-center hover:bg-black hover:scale-110 transition-all duration-200 shadow-sm border border-neutral-700"
                aria-label="TikTok"
              >
                <i className="fab fa-tiktok text-white text-sm"></i>
              </a> */}
            </div>
          </div>
        </div>
      </div>

      {/* Main Header - Enhanced Design */}
      <div className="py-4 px-4 border-b border-neutral-800 bg-neutral-900">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-xl hover:bg-neutral-800 transition-colors"
            aria-label="Toggle menu"
          >
            <Menu className="w-6 h-6 text-white" />
          </button>

          {/* Logo - Enhanced */}
          <Link to="/" className="flex items-center space-x-3 group shrink-0">
            <img
              src="/assets/Images/logo/blackstore.png"
              alt="BlackStore Logo"
              className="w-12 h-12 rounded-lg object-cover"
            />
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-white">BlackStore</h1>
              <p className="text-xs text-neutral-400">Premium Shopping</p>
            </div>
          </Link>

          {/* Mobile Search Button */}
          <button
            onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-xl hover:bg-neutral-800 transition-colors"
            aria-label="Search"
          >
            <Search className="w-5 h-5 text-white" />
          </button>

          {/* Desktop Search Bar - Enhanced */}
          <div className="flex-1 max-w-2xl mx-4 lg:mx-8 relative hidden md:block">
            <div className="relative group">
              <input
                type="text"
                placeholder="Search for products, brands and more..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-5 py-3.5 pr-14 border-2 border-neutral-700 bg-neutral-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm text-white placeholder:text-neutral-500 transition-all group-hover:border-neutral-600"
                onFocus={() => searchQuery && setShowSearchResults(true)}
                onKeyDown={handleSearchEnter}
              />
              <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-linear-to-r from-primary-dark to-primary text-black p-2.5 rounded-lg hover:from-primary-darker hover:to-primary-dark transition-all duration-200 shadow-md hover:shadow-lg">
                {searchLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </button>
            </div>

            {/* Search Results Dropdown */}
            {showSearchResults && (
              <div className="absolute top-full left-0 right-0 bg-neutral-900 border border-neutral-700 shadow-xl z-50 mt-1 rounded-lg max-h-96 overflow-y-auto overflow-x-hidden">
                <div className="p-2">
                  {searchResults.length > 0 ? (
                    <>
                      <div className="text-sm text-neutral-400 mb-2 px-2">
                        {searchResults.length} results found
                      </div>
                      {searchResults.slice(0, 16).map((product) => (
                        <div
                          key={product.id}
                          className="flex  items-center p-3 hover:bg-neutral-800 rounded-lg cursor-pointer transition-colors "
                          onClick={() => {
                            setShowSearchResults(false);
                            setSearchQuery("");
                            navigate(`/product/${product.id}`);
                          }}
                        >
                          {product.image_paths &&
                          product.image_paths.length > 0 ? (
                            <img
                              src={
                                getPrimaryProductImageUrl(
                                  product.image_paths,
                                ) || ""
                              }
                              alt={product.name}
                              className="w-12 h-12 object-cover rounded-md mr-3"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-200 rounded-md mr-3 flex items-center justify-center">
                              <span className="text-neutral-500 text-xs">
                                No Image
                              </span>
                            </div>
                          )}
                          <div className="flex-1">
                            <div
                              className="font-medium text-white"
                              style={{
                                maxWidth: "2.5rem",
                                // overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {product.name}
                            </div>
                            <div
                              className="text-sm text-neutral-400"
                              style={{
                                maxWidth: "2.5rem",
                                // overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {product.description}
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="p-4 text-center text-neutral-400">
                      <div className="text-2xl mb-2">🔍</div>
                      <p className="text-sm font-medium">No products found</p>
                      <p className="text-xs mt-1">Try different keywords</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Action Buttons - Enhanced */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Wishlist */}
            <button
              onClick={handleWishlistClick}
              className="flex flex-col items-center space-y-1 px-2 py-1.5 rounded-xl hover:bg-neutral-800 transition-all group"
              aria-label="Wishlist"
            >
              <div className="relative">
                <Heart className="w-6 h-6 text-white group-hover:text-primary transition-colors" />
                {wishlistCount > 0 ? (
                  <span className="absolute -top-2 -right-2 bg-primary text-black rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shadow-md">
                    {wishlistCount}
                  </span>
                ) : null}
              </div>
              <span className="text-xs text-neutral-400 group-hover:text-primary font-medium hidden sm:block">
                Wishlist
              </span>
            </button>

            {/* Shopping Cart */}
            <button
              className="flex flex-col items-center space-y-1 px-2 py-1.5 rounded-xl hover:bg-neutral-800 transition-all group"
              onClick={handleCartClick}
              aria-label="Shopping cart"
            >
              <div className="relative">
                <ShoppingCart className="w-6 h-6 text-white group-hover:text-primary transition-colors" />
                {cartLoading ? (
                  <div className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center">
                    <Loader2 className="w-3 h-3 animate-spin text-neutral-400" />
                  </div>
                ) : cartCount > 0 ? (
                  <span className="absolute -top-2 -right-2 bg-primary text-black text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold shadow-md">
                    {cartCount}
                  </span>
                ) : null}
              </div>
              <span className="text-xs text-neutral-400 group-hover:text-primary font-medium hidden sm:block">
                Cart
              </span>
            </button>

            {/* Tracking */}
            <button
              className="flex flex-col items-center space-y-1 px-2 py-1.5 rounded-xl hover:bg-neutral-800 transition-all group"
              onClick={handleTrackingClick}
              aria-label="Track order"
            >
              <div className="relative">
                <Truck className="w-6 h-6 text-white group-hover:text-primary transition-colors" />
              </div>
              <span className="text-xs text-neutral-400 group-hover:text-primary font-medium hidden sm:block">
                Track
              </span>
            </button>

            {/* My Account */}
            <div className="relative user-menu-container">
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex flex-col items-center space-y-1 px-2 py-1.5 rounded-xl hover:bg-neutral-800 transition-all group"
                    aria-label="User menu"
                  >
                    <div className="w-9 h-9 bg-linear-to-br from-primary-dark to-primary rounded-full flex items-center justify-center text-black font-bold shadow-md group-hover:shadow-lg transition-all">
                      {(
                        user.user_metadata?.first_name?.charAt(0) ||
                        user.user_metadata?.full_name?.charAt(0) ||
                        user.email?.charAt(0)
                      )?.toUpperCase() || "U"}
                    </div>
                    <span className="text-xs text-neutral-400 font-medium hidden sm:block">
                      {user.user_metadata?.first_name ||
                        user.user_metadata?.full_name ||
                        user.email?.split("@")[0] ||
                        "Account"}
                    </span>
                  </button>

                  {/* User Dropdown Menu - Enhanced */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-neutral-900 rounded-xl shadow-2xl border border-neutral-700 py-2 z-50 overflow-hidden">
                      <div className="px-4 py-3 border-b border-neutral-700 bg-neutral-800">
                        <p className="text-sm font-semibold text-white truncate">
                          {user.user_metadata?.full_name ||
                            user.user_metadata?.first_name ||
                            user.email?.split("@")[0] ||
                            "User"}
                        </p>
                        <p className="text-xs text-neutral-400 mt-0.5 truncate">
                          {user.email}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          signOut();
                          setIsUserMenuOpen(false);
                        }}
                        className="w-full px-4 py-3 text-left text-sm text-neutral-300 hover:bg-neutral-800 hover:text-primary transition-colors flex items-center space-x-3"
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="font-medium">Sign Out</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/registration"
                  className="flex flex-col items-center space-y-1 px-3 py-2 rounded-xl bg-linear-to-r from-primary-dark to-primary hover:from-primary-darker hover:to-primary-dark transition-all shadow-md hover:shadow-lg group"
                >
                  <User className="w-5 h-5 text-black" />
                  <span className="text-xs text-black font-semibold hidden sm:block">
                    Sign In
                  </span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {isCategoriesVisible === true && (
        <Categories
          categoriesLoading={categoriesLoading}
          categoriesError={categoriesError}
          categoriesWithSubs={categoriesWithSubs}
        />
      )}
    </div>
  );
};

export default Header;
